import React from "react";

const MethodologyIntro: React.FC = () => (
  <>
    <p className="text-xs uppercase font-semibold tracking-widest text-[#9E1B50] mb-2">
      Methodology &amp; Technical Description
    </p>
    <h1 className="font-serif text-3xl md:text-4xl font-normal text-[#380C1E] mb-4 leading-tight">
      Where Is Your Burnout Coming From? Design, Scoring, and Limitations of a
      Six-Domain Burnout Source Inventory
    </h1>

    <dl className="text-xs text-[#380C1E]/70 mb-8 space-y-1 font-medium border-y border-[#E8DEC8] py-4">
      <div>
        <dt className="inline font-bold">Author: </dt>
        <dd className="inline">
          Ieshia McArthur (publishing as Ieshia Danielle)
        </dd>
      </div>
      <div>
        <dt className="inline font-bold">Affiliation: </dt>
        <dd className="inline">iamieshiadanielle.com, Atlanta, Georgia</dd>
      </div>
      <div>
        <dt className="inline font-bold">Version: </dt>
        <dd className="inline">1.0 (instrument version 4), September 2026</dd>
      </div>
      <div>
        <dt className="inline font-bold">Document type: </dt>
        <dd className="inline">
          Methodology and technical description. This is not a validation study.
        </dd>
      </div>
    </dl>

    <div className="space-y-6 text-sm md:text-base leading-relaxed text-[#380C1E]/90 font-light">
      <section>
        <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-2 pb-2">
          Abstract
        </h2>
        <p>
          Most established burnout instruments measure whether burnout is
          present and how severe it is. They were built for the workplace, and
          the World Health Organization's ICD-11 entry for burn-out is
          explicitly limited to the occupational context. The women this
          instrument was built for describe an exhaustion that does not stay at
          work: it moves between their beliefs, their relationships, their
          boundaries, their sense of purpose, their money, and their bodies. The
          Where Is Your Burnout Coming From? quiz is a 33-screen,
          self-administered inventory that asks a different question from the
          established scales. Rather than measuring how burned out a respondent
          is, its primary aim is attribution: which of six life domains
          (Beliefs, Belonging, Boundaries, Business, Budget, Body, referred to
          as the six B's) is carrying the most weight for this respondent right
          now. Thirty forced-choice paired statements produce a ranked profile
          across the six domains, and a separate three-item intensity block
          estimates how deep and how long-standing the burnout is. This paper
          documents the conceptual model, the item architecture, the exact
          scoring rules, the exclusions and assumptions the instrument depends
          on, its known limitations, and the validation work that has not yet
          been done. It is published so that readers, practitioners, and
          reviewers can evaluate the instrument on its actual design rather than
          on its marketing.
        </p>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
          1. Purpose and scope
        </h2>
        <p className="mb-3">
          The quiz is a reflection and triage tool offered free to the public at
          quiz.iamieshiadanielle.com. It is used to help a respondent name the
          domain of life most likely to be driving her exhaustion, and to route
          her to the part of a coaching curriculum that addresses that domain.
          It is not a diagnostic instrument, it is not a clinical screen, and it
          produces no clinical cutoffs.
        </p>
        <p>
          This document exists for three reasons. First, any instrument that
          tells a person something about herself owes her a plain account of how
          that conclusion was reached. Second, transparency about scoring,
          exclusions, and assumptions is the only honest basis on which a
          self-assessment can ask to be trusted. Third, the author intends to
          collect response data and publish validation results, and this
          document fixes the design in writing before that data exists, so that
          later claims can be checked against it.
        </p>
      </section>
    </div>
  </>
);

export default MethodologyIntro;
